/**
 * Run-scoped enum vocabularies mirrored from `content_core.models.enums`.
 *
 * Kept apart from `content.types.ts` so the run detail page can grow its own
 * vocabulary without touching the shared wire types. Like the rest of the
 * content types, response fields stay typed as `string`: these constants are
 * the values this build KNOWS, not a guarantee about what the service sends.
 */

/**
 * Why one product/field failed to generate (`FailureReason`, `enums/content.py`).
 * The service splits these by what an operator would do next, not by which
 * exception fired.
 */
export const FAILURE_REASONS = [
  'SOURCE_MISSING',
  'SOURCE_INVALID',
  'MODEL_ERROR',
  'MODEL_TIMEOUT',
  'RATE_LIMITED',
  'CONTENT_FILTERED',
  'VALIDATION_FAILED',
  'GATEWAY_ERROR',
  'CANCELLED',
  'UNKNOWN',
] as const;
export type FailureReason = (typeof FAILURE_REASONS)[number];

/**
 * What the parent workflow is DOING right now (`RunStage`, `enums/run.py`) —
 * coarser than status; FANNING_OUT vs AWAITING_BATCHES tells "children not
 * starting" from "children not finishing" when a run looks stuck.
 */
export const RUN_STAGES = [
  'QUEUED',
  'PLANNING',
  'FANNING_OUT',
  'AWAITING_BATCHES',
  'PAUSED',
  'FINALIZING',
  'DONE',
] as const;
export type RunStage = (typeof RUN_STAGES)[number];

/** What sort of actor fired a run (`ActorKind`, `enums/trigger.py`). */
export const ACTOR_KINDS = ['USER', 'SERVICE', 'SCHEDULE', 'SYSTEM', 'UNKNOWN'] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];

/** Canonical run metric names (`generation_metrics.py`) — units live in the name. */
export const RUN_METRIC = {
  durationMs: 'run_duration_ms',
  productsTotal: 'products_total',
  batchesTotal: 'batches_total',
  batchesFailed: 'batches_failed',
  itemsGenerated: 'items_generated',
  itemsFailed: 'items_failed',
  promptTokensTotal: 'prompt_tokens_total',
  completionTokensTotal: 'completion_tokens_total',
  avgItemLatencyMs: 'avg_item_latency_ms',
  /** 0–1 fraction: generated / (generated + failed). Written only at finalize. */
  itemSuccessRate: 'item_success_rate',
} as const;
